import React, { useContext, useMemo } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Table, Heading } from '@frontend/design-system';
import { InsuranceReportContext } from '../dental-insurance-eligibility-report/onederful-insurance-eligibility-report/context/OnederfulReportContext.provider';
import { filterCoinsuranceTableData } from '../helpers/get-extras.helper';
import { GridTableEnum } from '../types/tables-data-type';
import { whiteRoundedBg } from './common-styles';
import { GridContainerContext } from './grid-container';
import { SeeMoreOrLessButton } from './see-more-or-less-button';
import { Summary } from './summary';

type coinsuranceProps = {
  style?: SerializedStyles;
  isPrintingReport?: boolean;
};

export const CoinsuranceTable = React.memo(({ style, isPrintingReport = false }: coinsuranceProps) => {
  const { t } = useTranslation('insuranceEligibilityReport');
  const {
    coinsuranceTableData,
    coinsurancesTableColConfigs,
    shouldShowCoinsurance,
    coinsuranceSummary,
    showNetworkFilter,
    filters,
    selectedNetworkFilterValue,
  } = useContext(InsuranceReportContext);
  const { selected, setSelected, selectedStyle, notSelectedStyle } = useContext(GridContainerContext);

  const isSelected = selected === GridTableEnum.COINSURANCE;
  const isNotSelected = !!selected && selected !== GridTableEnum.COINSURANCE;

  const filteredTableData = useMemo(
    () =>
      filterCoinsuranceTableData({
        data: coinsuranceTableData,
        filters,
        showNetworkFilter,
        applyFilters: !isPrintingReport,
      }),
    [coinsuranceTableData, filters, showNetworkFilter, isPrintingReport]
  );

  if (shouldShowCoinsurance) {
    return (
      <motion.div
        css={[containerStyles, whiteRoundedBg, isSelected && selectedStyle, isNotSelected && notSelectedStyle, style]}
        layout
      >
        <motion.div layout='position' css={headingContainerStyle}>
          <Heading level={2}>{t('Coinsurance')}</Heading>
          <SeeMoreOrLessButton
            onClick={() => setSelected(isSelected ? '' : GridTableEnum.COINSURANCE)}
            isSelected={isSelected}
          />
        </motion.div>
        <Summary
          expanded={isSelected}
          applyFilters={!isPrintingReport}
          data={coinsuranceSummary ?? []}
          noDataFoundMessage={t('No data found for {{selectedNetworkFilterValue}}', { selectedNetworkFilterValue })}
        />
        <Table
          colConfig={coinsurancesTableColConfigs}
          data={filteredTableData}
          hasResponsiveColWidths
          isFocusable
          isSelectable={false}
          hasGlobalSearch
          globalSearchConfig={{
            initialValue: '',
          }}
          wrapperStyle={wrapperStyle}
          isPaginated={!isPrintingReport && isSelected && coinsuranceTableData && coinsuranceTableData?.length > 10}
        />
      </motion.div>
    );
  }
  return null;
});

CoinsuranceTable.displayName = 'CoinsuranceTable';

const containerStyles = css`
  @media (max-width: 900px) {
    grid-column: 1/3;
  }
`;

const headingContainerStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const wrapperStyle = css`
  border-bottom-left-radius: ${theme.borderRadius.small};
  border-bottom-right-radius: ${theme.borderRadius.small};
  overflow: hidden;
  background-color: ${theme.colors.white};
  padding: ${theme.spacing(1, 0, 0, 0)};
`;
